/**
 * React state for self-schedule — thin selector over the pure
 * pipeline output. Owns:
 *   1. `DayStateMap` (per-day isSelected + customSlots + removedIds)
 *   2. Memoised pipeline run when inputs change
 *   3. Derived UI metadata (daysMeta, master-checkbox state)
 *   4. Mutation callbacks (toggleDay, toggleAll, removeSlot, …)
 *   5. A stable `onSlotsChange` emit keyed by the flat active list
 *
 * The pipeline itself has no React dependency — this hook is the
 * single React-aware layer. Custom slots stretch to the current
 * duration on duration change (legacy parity).
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import moment from 'moment-timezone';
import type { BusyEvent, Interval } from '../../../availability';
import { runSlotPipeline } from '../pipeline';
import type { DaySlotMap, SelfSlot } from '../types';
import { seedFromInitial, type InitialSlotsPayload } from './seedFromInitial';
import type { DayState, DayStateMap } from './types';

export interface UseSelfScheduleSlotsArgs {
  availability: Interval[];
  busyEvents: BusyEvent[];
  duration: number;
  timezone: string;
  minimumNoticeHours?: number;
  /** Whether any interviewer is picked — gates pipeline execution. */
  hasInterviewer: boolean;
  /** Edit-mode seed — mapped to custom slots. */
  initialSlots?: InitialSlotsPayload[];
  /** Fires whenever the emitted slot list actually changes. */
  onSlotsChange?: (_slots: Array<{ start: string; end: string }>) => void;
}

export interface DayMeta {
  isSelected: boolean;
  hasAvailability: boolean;
  slotCount: number;
}

export interface UseSelfScheduleSlotsResult {
  /** Active slots across all days — feeds FullCalendar events. */
  activeSlots: SelfSlot[];
  /** Per-day metadata keyed by `YYYY-MM-DD`. */
  daysMeta: Record<string, DayMeta>;
  totalActive: number;
  allChecked: boolean;
  allIndeterminate: boolean;
  toggleDay: (_dateKey: string, _checked: boolean) => void;
  toggleAll: (_checked: boolean) => void;
  removeSlot: (_slotId: string) => void;
  addCustomSlot: (_slot: SelfSlot) => void;
  clearAll: () => void;
}

function buildActiveSlots(
  state: DayStateMap,
  generated: DaySlotMap
): SelfSlot[] {
  const out: SelfSlot[] = [];
  for (const [dateKey, day] of Object.entries(state)) {
    if (!day.isSelected) continue;
    for (const slot of generated[dateKey] ?? []) {
      if (!day.removedIds.has(slot.id)) out.push(slot);
    }
    out.push(...day.customSlots);
  }
  return out;
}

function buildDaysMeta(
  state: DayStateMap,
  generated: DaySlotMap
): Record<string, DayMeta> {
  const meta: Record<string, DayMeta> = {};
  for (const [dateKey, slots] of Object.entries(generated)) {
    meta[dateKey] = {
      isSelected: state[dateKey]?.isSelected ?? false,
      hasAvailability: slots.length > 0,
      slotCount: slots.length,
    };
  }
  for (const dateKey of Object.keys(state)) {
    if (meta[dateKey]) continue;
    meta[dateKey] = {
      isSelected: state[dateKey]?.isSelected ?? false,
      hasAvailability: false,
      slotCount: 0,
    };
  }
  return meta;
}

export function useSelfScheduleSlots({
  availability,
  busyEvents,
  duration,
  timezone,
  minimumNoticeHours = 0,
  hasInterviewer,
  initialSlots,
  onSlotsChange,
}: UseSelfScheduleSlotsArgs): UseSelfScheduleSlotsResult {
  const [dayState, setDayState] = useState<DayStateMap>(() =>
    seedFromInitial(initialSlots)
  );

  // Pure pipeline re-runs whenever its inputs move. The hook is just
  // a thin cache over the result; `dayState` is untouched so user
  // selections survive availability updates.
  const generatedByDay = useMemo(
    () =>
      hasInterviewer
        ? runSlotPipeline({
            availability,
            busyEvents,
            duration,
            timezone,
            minimumNoticeHours,
          })
        : {},
    [
      availability,
      busyEvents,
      duration,
      timezone,
      minimumNoticeHours,
      hasInterviewer,
    ]
  );

  const activeSlots = useMemo(
    () => buildActiveSlots(dayState, generatedByDay),
    [dayState, generatedByDay]
  );

  const daysMeta = useMemo(
    () => buildDaysMeta(dayState, generatedByDay),
    [dayState, generatedByDay]
  );

  const { allChecked, allIndeterminate } = useMemo(() => {
    const availableKeys = Object.keys(daysMeta).filter(
      (k) => daysMeta[k]?.hasAvailability
    );
    if (availableKeys.length === 0) {
      return { allChecked: false, allIndeterminate: false };
    }
    const selected = availableKeys.filter(
      (k) => daysMeta[k]?.isSelected
    ).length;
    return {
      allChecked: selected === availableKeys.length,
      allIndeterminate: selected > 0 && selected < availableKeys.length,
    };
  }, [daysMeta]);

  // Emit only on a real change — serialising the flat list keeps us
  // from firing on unrelated re-renders (e.g. parent state churn).
  const emitKey = useMemo(
    () => activeSlots.map((s) => `${s.start}|${s.end}`).join(','),
    [activeSlots]
  );
  useEffect(() => {
    onSlotsChange?.(activeSlots.map(({ start, end }) => ({ start, end })));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [emitKey]);

  const toggleDay = useCallback((dateKey: string, checked: boolean) => {
    setDayState((prev) => {
      const current: DayState = prev[dateKey] ?? {
        isSelected: false,
        removedIds: new Set(),
        customSlots: [],
      };
      return {
        ...prev,
        [dateKey]: checked
          ? { ...current, isSelected: true, removedIds: new Set() }
          : { ...current, isSelected: false, customSlots: [] },
      };
    });
  }, []);

  const toggleAll = useCallback(
    (checked: boolean) => {
      // Uncheck wipes everything — generated selections, custom
      // slots, removal history — across every day. Check selects
      // every day that has availability; custom-only days (no
      // generated slots for the week) are left untouched so
      // nothing springs back up unexpectedly.
      setDayState((prev) => {
        if (!checked) return {};
        const next: DayStateMap = { ...prev };
        const keys = Object.keys(generatedByDay).filter(
          (k) => (generatedByDay[k]?.length ?? 0) > 0
        );
        for (const dateKey of keys) {
          const current = next[dateKey] ?? {
            isSelected: false,
            removedIds: new Set<string>(),
            customSlots: [],
          };
          next[dateKey] = {
            ...current,
            isSelected: true,
            removedIds: new Set(),
          };
        }
        return next;
      });
    },
    [generatedByDay]
  );

  const removeSlot = useCallback((slotId: string) => {
    setDayState((prev) => {
      const next: DayStateMap = { ...prev };
      for (const [dateKey, state] of Object.entries(prev)) {
        if (state.customSlots.some((s) => s.id === slotId)) {
          next[dateKey] = {
            ...state,
            customSlots: state.customSlots.filter((s) => s.id !== slotId),
          };
          return next;
        }
        // Generated ids are namespaced `gen-{dateKey}-…` — only stash
        // into the matching day's removed set.
        if (slotId.startsWith(`gen-${dateKey}-`)) {
          const removedIds = new Set(state.removedIds);
          removedIds.add(slotId);
          next[dateKey] = { ...state, removedIds };
          return next;
        }
      }
      return prev;
    });
  }, []);

  const addCustomSlot = useCallback((slot: SelfSlot) => {
    setDayState((prev) => {
      const current = prev[slot.dateKey] ?? {
        isSelected: true,
        removedIds: new Set<string>(),
        customSlots: [],
      };
      return {
        ...prev,
        [slot.dateKey]: {
          ...current,
          isSelected: true,
          customSlots: [...current.customSlots, slot],
        },
      };
    });
  }, []);

  const clearAll = useCallback(() => setDayState({}), []);

  // Removing the last interviewer should reset every slot —
  // auto-generated or custom — since nothing is schedulable
  // without someone to interview.
  useEffect(() => {
    if (!hasInterviewer) setDayState({});
  }, [hasInterviewer]);

  // Legacy parity — keep custom slot ends in sync with the form's
  // duration so the emitted payload never mismatches.
  useEffect(() => {
    setDayState((prev) => {
      let touched = false;
      const next: DayStateMap = {};
      for (const [dateKey, state] of Object.entries(prev)) {
        const nextCustom = state.customSlots.map((slot) => {
          const startMs = moment.tz(slot.start, timezone).valueOf();
          const targetEnd = moment
            .tz(startMs + duration * 60_000, timezone)
            .toISOString();
          if (targetEnd === slot.end) return slot;
          touched = true;
          return { ...slot, end: targetEnd };
        });
        next[dateKey] =
          nextCustom === state.customSlots
            ? state
            : { ...state, customSlots: nextCustom };
      }
      return touched ? next : prev;
    });
  }, [duration, timezone]);

  return {
    activeSlots,
    daysMeta,
    totalActive: activeSlots.length,
    allChecked,
    allIndeterminate,
    toggleDay,
    toggleAll,
    removeSlot,
    addCustomSlot,
    clearAll,
  };
}
